<template>
	<DefaultSection v-if="matchedFeatureDisplayData.length">
		<DefaultHeading class="px-2" :size="2" :horizontal-line="false">
			Top features
		</DefaultHeading>
		<div class="top-features w-full">
			<Carousel
				ref="carouselRef"
				:settings="carouselSettings"
				:breakpoints="carouselBreakpoints"
			>
				<template #addons="{ slidesCount, currentSlide }">
					<ClientOnly>
						<Navigation
							:class="{
								'is-first-slide': !currentSlide,
								'is-last-slide':
									currentSlide === slidesCount - itemsToShow,
							}"
						/>
					</ClientOnly>
				</template>
				<Slide
					v-for="displayData in matchedFeatureDisplayData"
					:key="displayData.title"
				>
					<div
						class="carousel__item flex items-start justify-between px-3 py-1"
					>
						<CarbarFeatureOne>
							<template #icon>
								<CarbarImage
									:image="displayData.image"
									:alt-text="displayData.title"
								/>
							</template>
							<template #title>
								<div
									class="mt-2 text-center text-xl font-medium text-gray-700"
								>
									{{ displayData.title }}
								</div>
							</template>
							<template #subtitle>
								<div class="text-base text-gray-575">
									{{ displayData.subtitle }}
								</div>
							</template>
						</CarbarFeatureOne>
					</div>
				</Slide>
			</Carousel>
		</div>
	</DefaultSection>
</template>

<script setup>
import DefaultHeading from '@/components/headings/DefaultHeading.vue'
import CarbarImage from '@/components/CarbarImage.vue'
import CarbarFeatureOne from '@/components/vehicle-details/cards/FeatureOne.vue'
import DefaultSection from '@/components/sections/DefaultSection.vue'

const itemsToShow = ref(3)

const props = defineProps({
	product: {
		type: Object,
		required: false,
	},
})

const features = computed(() => props.product.features ?? null)

const featureDisplayData = [
	{
		label: ['Smart Device Integration - Android Auto'],
		image: 'img/product-details/featured/img-smartphone.webp',
		title: 'Smart phone integration - Apple carplay',
		subtitle: '',
	},
	{
		label: ['Smart Device Integration - Apple CarPlay'],
		image: 'img/product-details/featured/img-smartphone.webp',
		title: 'Smart phone integration - Android auto',
		subtitle: '',
	},
	{
		label: [
			'Keyless Card - with remote entry',
			'Keyless Start - Key/FOB Proximity related',
		],
		image: 'img/product-details/featured/img-keyless_entry.webp',
		title: 'Keyless entry',
		subtitle: '',
	},
	{
		label: [
			'Leather seats',
			'Leather Seats - Embossed',
			'Leather Seats - Nappa',
			'Leather Seats - Partial',
			'Leather Seats - Perforated',
		],
		image: 'img/product-details/featured/img-leatherupholstery.webp',
		title: 'Leather seats',
		subtitle: 'Maintains a constant distance from the vehicle in front',
	},
	{
		label: [
			'Sunroof',
			'Sunroof - Electric',
			'Sunroof - Electric Dual/ Twin',
			'Sunroof - Electric Remote Control Open/Close',
		],
		image: 'img/product-details/featured/img-panoramic.webp',
		title: 'Sunroof',
		subtitle:
			'The generous glass sunroof not only emphasises the vehicle design, but also enhances the unique feeling of space.',
	},
	{
		label: [
			'Parking Assist - Graphical Display',
			'Parking Assistance - Automated Steering',
			'Parking Assistance - Fully Automated',
		],
		image: 'img/product-details/featured/img-parkingsensor.webp',
		title: 'Parking assist',
		subtitle: '',
	},
	{
		label: ['Camera - Rear Vision'],
		image: 'img/product-details/featured/img-reverse-sensor.webp',
		title: 'Camera - Rear vision',
		subtitle: '',
	},
	{
		label: ['GPS (Satellite Navigation)'],
		image: 'img/product-details/featured/img-sat_nav.webp',
		title: 'Sat nav',
		subtitle: '',
	},
	{
		label: [
			'Tow Bar',
			'Towing - Latch/Hook Front',
			'Towing - Latch/Hook Rear',
			'Towing - Trailer Socket',
		],
		image: 'img/product-details/featured/img-towbar.webp',
		title: 'Towbar',
		subtitle: '',
	},
	{
		label: [
			'Cruise Control',
			'Cruise Control - Distance Control',
			'Cruise Control - low speed Offroad (Brake & Accel)',
			'Cruise Control - with Brake Function (limiter)',
		],
		image: 'img/product-details/featured/img-cruise_control.webp',
		title: 'Cruise control',
		subtitle: '',
	},
]

const carouselRef = ref(null)

const matchedFeatureDisplayData = computed(() => {
	if (!features.value || !Array.isArray(features.value)) {
		return []
	}

	// using item.value here because  the product team have provided generic names for the features
	const featureLabels = features.value
		.map((item) => `${item.label}`.trim().toLowerCase())
		.filter((label) => label !== null)

	const displayData = featureDisplayData.filter((dataItem) => {
		const featureDisplayDataLabels = Array.isArray(dataItem.label)
			? dataItem.label
			: [dataItem.label]

		let matched = false
		featureDisplayDataLabels.forEach((labelItem) => {
			if (featureLabels.includes(`${labelItem}`.toLowerCase())) {
				matched = true
			}
		})

		return matched
	})

	return displayData
})

const carouselSettings = {
	itemsToShow: 1,
	mouseDrag: false,
	snapAlign: 'start',
	wrapAround: false,
}

const carouselBreakpoints = {
	1: {
		itemsToShow: 1.1,
		snapAlign: 'start',
	},
	700: {
		itemsToShow: matchedFeatureDisplayData.value.length <= 2 ? 2 : 2,
		snapAlign: 'start',
	},
	1024: {
		itemsToShow: matchedFeatureDisplayData.value.length <= 3 ? 3 : 3,
		snapAlign: 'start',
	},
}
</script>
